import * as _ from "lodash";
import numeral from "numeral";

import { GfxUtils, NodeType } from "../../../common/gfxUtils";
import { ViewerUtils } from "../../../common/viewerUtils";
import { PropertyDefinition } from "../../../smartDrawings";
import { UnitConstants } from "../../../common/constants";
import { viewerOptions } from "../../../viewer";
import * as SmartDrawings from "../../../smartDrawings";

export let propertyInfo: PropertyCollectingInfo[] = [];

export class BalconyEvents {
    static SD_BALCONY_DATA_UPDATED = "SD_BALCONY_DATA_UPDATED";
}

export function getPropertyInfoItem(key: string) {
    return _.find(propertyInfo, prop => key === prop.propertyDefinition.name);
}

export interface RecognitionData {
    id: string;
    sourceRecognition: ObjectRecognitionData;
    propertyInfoFilename: string;
    objectRecognition: ObjectRecognitionData[];
}

export interface ObjectRecognitionData {
    typeCategory: string;
    typeProperty: string;
    typeValue: string;
    category: string;
    property: string;
    value: string;
    exactMatch: boolean;
}

export interface PropertyCollectingInfo {
    id: string;
    category: string;
    property: string;
    required: boolean;
    propertyDefinition: PropertyDefinition;
    reportInfo: PropertyReportInfo;
}

export interface PropertyReportInfo {
    includeInClassification: boolean;
    includeInReport: boolean;
    reportGroup: string;
    reportName: string;
    order: number;
    reportValue?: string | number;
}

export interface SavedClasses {
    type: string;
    groups: number[];
    color: THREE.Color;
}

export enum BalconyNodeType {
    SingleGlass,
    GlassRow,
    BalconyGlass,
    RailingGlass
}

export interface INode {
    id: number;
    color: THREE.Color;
    position: THREE.Vector3;
    connectingIds: number[];
    propertyValues?: { [key: string]: string };
}

export interface IBalconyInfo {
    shapeNodes: INode[];
    nodes: { [key: number]: BalconyNode };
    groups: BalconyGroup[]; // (koko parveke) toistaiseksi vain kaiteen yläpuoliset lasit
    classes: IBalconyClass[]; //(esim. luokan A parveke)

    propertyInfo: PropertyCollectingInfo[];

    addNode(type: BalconyNodeType, connectingObjectIds: number[]);
    drawNodes();
    getAllNodes(): Promise<INode[]>;
    getNodes(ids: number[]): Promise<BalconyNode[]>;
    getNodesConnectingIds(id: number): number[];
    removeNode(id: number);
    removeNodeShape(id: number);
    addGroup(balconyNodes: BalconyNode[]): number;
    addGroupWithConnectingIds(connectingObjectIds: number[], properties: Autodesk.Viewing.Property[]): BalconyGroup;
    addGroups(groups: BalconyGroup[]);
    drawGroups();
    getGroup(id: number): BalconyGroup;
    changeGroupColor(group: BalconyGroup, color: THREE.Color);
    removeGroup(id: number);
    classifyGroups();
    createReportInfo(propertyInfo: PropertyCollectingInfo[]): Promise<any[]>;
    addClasses(classData: SavedClasses[]);

    reset();

    setUpdated(value: boolean);


    getDataAsObject();
    getDataAsJson();
}

export class BalconyInfo implements IBalconyInfo {
    shapeNodes: INode[];
    nodes: { [index: number]: BalconyNode; };
    groups: BalconyGroup[];
    classes: IBalconyClass[];
    propertyInfo: PropertyCollectingInfo[];

    private viewer: Autodesk.Viewing.GuiViewer3D;
    private gfxUtils: GfxUtils;
    gfxUtilsPanels: GfxUtils;

    private updated: boolean = false;

    constructor(viewer: Autodesk.Viewing.GuiViewer3D) {
        //this.shapeNodes = [];
        this.nodes = {};
        this.groups = [];
        this.classes = [];
        this.propertyInfo = [];
        this.viewer = viewer;
        this.gfxUtils = new GfxUtils(this.viewer, "BalconyInfo");
        this.gfxUtilsPanels = new GfxUtils(this.viewer, "BalconyPanels");
    }

    public getDataAsObject() {
        var classInfos: SavedClasses[] = this.classes.map(c => {
            return { type: c.classType, groups: c.groups.map(g => g.id), color: c.color };
        });

        var obj = {
            groups: this.groups,
            classes: classInfos
        };

        return obj;
    }

    public getDataAsJson() {
        let json = JSON.stringify(this.getDataAsObject(), null, 2);
        return json;
    }

    public setUpdated(value: boolean = true) {
        this.viewer.dispatchEvent({ type: BalconyEvents.SD_BALCONY_DATA_UPDATED, updated: value });

        this.updated = value;
    }

    public isUpdated() {
        return this.updated;
    }

    public addNode(type: BalconyNodeType, connectingObjectIds: number[]): number {
        let newId = this.nextFreeNodeId();
        let node = new BalconyNode(this.viewer, newId, connectingObjectIds, type);
        this.nodes[newId] = node;
        //this.shapeNodes.push(node);
        return newId;
    }

    public async drawNodes() {
        let drawNodes = {};
        for (let key in this.nodes) {
            drawNodes[this.nodes[key].id] = ({
                X: this.nodes[key].position.x,
                Y: this.nodes[key].position.y,
                Z: this.nodes[key].position.z
            });
        }

        await this.gfxUtils.drawConnectingNodes(drawNodes, _.values(this.nodes)[0].color, NodeType.Sphere);
    }

    public async getAllNodes(): Promise<INode[]> {
        return this.groups;
    }

    public async getNodes(ids: number[]): Promise<BalconyNode[]> {

        let nodes = [];
        ids.forEach(id => nodes.push(_.find(this.nodes, node => node.id === id)));
        return nodes;
    }

    public getNodesConnectingIds(id: number): number[] {
        //return _.find(this.shapeNodes, node => node.id === id).connectingIds;
        return _.find(this.groups, group => group.id === id).connectingIds;
    }

    public removeNode(id: number) {
        //this.shapeNodes = this.shapeNodes.filter(node => node.id !== id);
        //delete this.nodes[id];
        //this.gfxUtils.deleteNode(id);
        this.removeGroup(id);
    }

    public removeNodeShape(id: number) {
        this.gfxUtils.deleteNode(id);
    }

    public getInfoNodeModel(): Autodesk.Viewing.Model {
        return this.gfxUtils.getModel();
    }

    public getInfoNodeModelNumber(): number {
        return this.gfxUtils.getModelId();
    }

    public addGroup(balconyNodes: BalconyNode[]): number {
        return null;
    //    let cIds = [];
    //    for (let node of balconyNodes) {
    //        cIds.push(...node.connectingIds);
    //    }

    //    let newId = this.nextFreeNodeId();
    //    let group: BalconyGroup = new BalconyGroup(this.viewer, newId, cIds, balconyNodes);
    //    this.groups.push(group);
    //    this.shapeNodes.push(group);
    //    return newId;
    }

    public addGroupWithConnectingIds(connectingObjectIds: number[], properties: Autodesk.Viewing.Property[]): BalconyGroup {
        let newId = this.nextFreeNodeId();
        let group = new BalconyGroup(this.viewer, newId, connectingObjectIds, properties);
        this.groups.push(group);
        //this.shapeNodes.push(group);

        this.setUpdated();

        return group;
    }

    public addGroupWithPanels(panels: BalconyPanel[], properties: { [key: string]: string }): BalconyGroup {
        let newId = this.nextFreeNodeId();
        let group = new BalconyGroup(this.viewer, newId, panels.map(i => i.id), null, properties, true,
            true, panels);

        //group.propertyValues = properties;

        this.groups.push(group);

        this.setUpdated();

        return group;
    }

    public async addGroups(groups: BalconyGroup[]) {
        for (let group of groups) {
            let newGroup = new BalconyGroup(this.viewer, group.id, group.connectingIds,
                null, group.propertyValues, false, group.manual, group.panels);

            newGroup.position = group.position;
            newGroup.color = group.color;
            newGroup.measurements = null;
            if (group.measurements) {
                newGroup.measurements = group.measurements;
            }
            
            this.groups.push(newGroup);
        }

        this.setUpdated();
    }

    public updateGroupMeasurements(groupId: number, meas) {
        let group = this.getGroup(groupId);
        if (!group) return;

        group.measurements = meas;

        this.setUpdated();
    }

    public copyGroupWithZOffset(group: BalconyGroup, offset: number, amount: number): BalconyGroup[] {
        let newGroups: BalconyGroup[] = [];

        let nextPanelId = this.nextPanelId(group.panels[0].modelId);
        let panelsCount = group.panels.length;

        for (var i = 1; i <= amount; i++) {
            let newId = this.nextFreeNodeId();

            let newProps = { ...group.propertyValues };
            newProps["Apartment"] = "Copied balcony";
            if (newProps["Level"].length > 0) {
                let level = Number(newProps["Level"]);
                level = level + (offset < 0 ? -1 : 1) * i;
                newProps["Level"] = level.toString();
            }

            let newGroup = group.copyWithZOffSet(newId, i * offset, nextPanelId, newProps);

            (async () => await newGroup.drawPanels(this.gfxUtilsPanels))();

            nextPanelId += panelsCount;

            newGroups.push(newGroup);
            this.groups.push(newGroup);
        }

        this.setUpdated();

        return newGroups;
    }

    private nextPanelId(modelId: number): number {
        let panelsModel = this.viewer.getAllModels().find(m => m.id === modelId);
        let ids = _.keys(panelsModel.getInstanceTree().nodeAccess.dbIdToIndex).map(i => Number(i));
        let max = Math.max(...ids);
        return max > 0 ? max + 1 : 1;
    }

    public async drawGroups(): Promise<number> {
        if (this.groups.length > 0) {
            let drawNodes = {};
            for (let group of this.groups) {
                // Draw panels if group is manually created
                if (group.panels)
                    await group.drawPanels(this.gfxUtilsPanels);

                if (group.position) {
                    drawNodes[group.id] = {
                        X: group.position.x,
                        Y: group.position.y,
                        Z: group.position.z
                    };
                }
            }

            return await this.gfxUtils.drawConnectingNodes(drawNodes, this.groups[0].color, NodeType.Sphere);
        }
    }

    public drawGroupInfoNode(group: BalconyGroup) {
        // Remove old one just to make sure
        this.gfxUtils.deleteNode(group.id);

        if (group.position) {
            let drawNodes = {};

            drawNodes[group.id] = {
                X: group.position.x,
                Y: group.position.y,
                Z: group.position.z
            };

            (async () => await this.gfxUtils.drawConnectingNodes(drawNodes, group.color, NodeType.Sphere))();
        }

    }

    public getGroup(id: number): BalconyGroup {
        return this.groups.find(g => g.id === id);
    }

    public changeGroupColor(group: BalconyGroup, color: THREE.Color) {
        this.gfxUtils.changeNodeColor(group.id, color);
    }

    public removeGroup(id: number) {
        //this.shapeNodes = this.shapeNodes.filter(node => node.id !== id);

        (async () => await this.getGroup(id).removePanels(this.gfxUtilsPanels))();

        this.groups = this.groups.filter(group => group.id !== id);
        if (this.classes.length > 0) {
            for (let c of this.classes) {
                c.groups = c.groups.filter(group => group.id !== id);
            }

            // Delete class if no groups included
            this.classes = this.classes.filter(c => c.groups.length > 0);
        }

        this.gfxUtils.deleteNode(id);

        if (this.groups.length == 0) {
            //this.gfxUtils.deleteModel();
            //this.gfxUtilsPanels.deleteModel();
        }

        this.setUpdated();
    }

    public async classifyGroups() {
        this.classes = [];
        // Get group id's to go through
        let groupIds = this.groups.map(group => group.id);

        // Compare groups
        let length = groupIds.length;
        while (length > 0) {
            // Add first group into classes
            let classGroup = this.getGroup(groupIds[0]);
            let classIndex = this.classes.push({ classType: this.nextClassType(), groups: [classGroup], color: this.classColor(this.classes.length) }) - 1;
            groupIds.splice(0, 1);
            length--;
            for (let i = length - 1; i >= 0; i--) {
                let groupToCompare = this.getGroup(groupIds[i]);
                if (await classGroup.equals(groupToCompare)) {
                    this.classes[classIndex].groups.push(groupToCompare);
                    groupIds.splice(i, 1);
                    length--;
                }
            }
        }

        // Change group colors
        await this.updateGroupColors();

        this.setUpdated();
    }

    public async findMatchingGroups(propId: string, propValue: string): Promise<BalconyGroup[]> {
        let found = this.groups.filter(g => {
            let propInfo = this.propertyInfo.find(prop => propId === prop.id);
            if (propInfo) {
                let name = propInfo.propertyDefinition.name;
                //return g.propertyValues.hasOwnProperty(name) && g.propertyValues[name] === propValue;
                return g.propertyValues[name] === propValue;
            }
        });

        return found;
    }

    public async createReportInfo(): Promise<any[]> {
        let startRow = 2;

        //let reportInfos = this.propertyInfo.filter(i => i.reportInfo.includeInReport).map(i => i.reportInfo).sort(
        //    (a, b) => { return a.order - b.order; });

        let reportInfos = propertyInfo.filter(i => i.reportInfo.includeInReport).sort(
            (a, b) => { return a.reportInfo.order - b.reportInfo.order; });

        let columns = reportInfos.map(r => r.reportInfo.reportName);

        let headers = columns.slice(0);
        headers.unshift("Class");

        const info = [];
        info.push(headers as any);

        this.classes.forEach(c => {
            for (let group of c.groups) {
                let row: any[] = [`${c.classType}`];
                for (let info of reportInfos) {
                    const found = group.propertyValues.hasOwnProperty(info.propertyDefinition.name); //.reportData.find(r => r.reportName === column);
                    if (found)
                        /*row.push(String(group.reportData.find(r => r.reportName === column).reportValue));*/
                        row.push(group.propertyValues[info.propertyDefinition.name]);
                    else
                        row.push(null);
                }
                info.push(row as any);
            }

            //Add empty row
            info.push([] as any);
        });

        return info;
    }

    public async addClasses(classData: SavedClasses[]) {
        for (let c of classData) {
            this.classes.push({ classType: c.type, groups: c.groups.map(g => this.getGroup(g)), color: c.color });
        }

        await this.updateGroupColors();

        this.setUpdated();
    }

    public reset() {
        for (let group of this.groups) {
            if (!group.manual) {
                this.removeGroup(group.id);
            }
        }

        if (this.groups.length == 0) {
            // Because of viewer bug, you have to remove the whole model
            this.gfxUtils.deleteModel();

            //this.shapeNodes = [];
            this.nodes = {};
            this.groups = [];
            this.classes = [];
        }
    }

    private nextFreeNodeId() {
        return this.groups.length > 0 ? _.max(_.map(this.groups, node => node.id)) + 1 : 1;
    }

    private nextClassType(): string {
        //let str = this.classes.length > 0 ? this.classes[this.classes.length - 1].classType : "";
        //return str.length > 0 ? String.fromCharCode(str.charCodeAt(str.length - 1) + 1) : "A";

        return (this.classes.length + 1).toString();
    }

    private classColor(index: number): THREE.Color {
        return new THREE.Color(`rgb(${this.rgbPart()},${this.rgbPart()},${this.rgbPart()})`);
    }

    private rgbPart(): number {
        return Math.floor(Math.random() * 255); 
    }

    private async updateGroupColors() {
        this.classes.forEach(c => {
            c.groups.forEach(group => {
                this.changeGroupColor(group, c.color);
            });
        });
    }
}

interface IBalconyGroup extends INode {
    nodes: BalconyNode[];
    //reportData: PropertyReportInfo[];

    equals(group: BalconyGroup): Promise<boolean>;
}

export class BalconyGroup implements IBalconyGroup {
    id: number;
    color: THREE.Color;
    position: THREE.Vector3;
    connectingIds: number[];
    propertyValues: { [key: string]: string } = {};

    // Manual workflow
    manual: boolean;
    panels: BalconyPanel[];
    measurements: any;

    nodes: BalconyNode[];
    //reportData: PropertyReportInfo[] = [];

    constructor(forgeViewer: Autodesk.Viewing.GuiViewer3D, nodeId: number, connectedParts: number[],
        properties?: Autodesk.Viewing.Property[],
        propertyValues?: { [key: string]: string }, calcPosition: boolean = true,
        createdManually = false, glassPanels: BalconyPanel[] = []) {
        this.id = nodeId;
        this.color = new THREE.Color(1, 0, 1);
        this.connectingIds = connectedParts;
        if (properties)
            this.initAdskProperties(properties);
        else if (propertyValues) {
            this.propertyValues = { ...propertyValues };
            //this.initReportInfo(propertyInfo);
        }

        this.manual = createdManually;
        if (glassPanels.length > 0) {
            this.panels = [];
            glassPanels.forEach(p => {
                let panel = new BalconyPanel(p.id, this.objectToVector3(p.baseLine.start),
                    this.objectToVector3(p.baseLine.end), p.height, this.objectToColor(p.color), p.modelId);
                this.panels.push(panel);
            });
        }

        if (calcPosition)
            this.position = this.calcShapePosition(forgeViewer);
    }

    public copyWithZOffSet(newId: number, offset: number, nextPanelId: number, propertyValues?: { [key: string]: string }): BalconyGroup {
        let offsetVector = new THREE.Vector3(0, 0, offset);
        let panels: BalconyPanel[] = [];
        if (this.panels.length > 0) {
            for (var i = 0; i < this.panels.length; i++) {
                panels.push(this.panels[i].copyWithOffSet(nextPanelId, offsetVector));
                nextPanelId++;
            }
        }

        let group = new BalconyGroup(null, newId, panels.map(i => i.id), null, propertyValues ?? this.propertyValues,
            true, true, panels);

        // Copy measurements
        if (this.measurements && this.measurements.length > 0) {
            let newMeasurements = [];
            for (let meas of this.measurements) {
                let newMeas = {
                    type: meas.type,
                    precision: meas.precision,
                    value: meas.value,
                    unit: meas.unit,
                    picks: []
                };

                for (let pick of meas.picks) {
                    let newPick = {
                        intersection: this.objectToVector3(pick.intersection).add(offsetVector),
                        modelId: pick.modelId,
                        viewportIndex2d: pick.viewportIndex2d
                    };

                    newMeas.picks.push(newPick);
                }

                newMeasurements.push(newMeas);
            }

            group.measurements = newMeasurements;
        }

        return group;
    }

    private initAdskProperties(properties: Autodesk.Viewing.Property[]) {
        for (let info of propertyInfo) {
            let adskProp = _.find(properties, prop => prop.displayCategory === info.category && prop.displayName === info.property);
            if (adskProp) {
                //this.propertyValues[info.propertyDefinition.name] = adskProp.displayValue.toString();
                let propValue = this.formatValue(info.propertyDefinition.name, adskProp.displayValue.toString());
                //let item = Object.assign({}, info.reportInfo);
                //if (info.propertyDefinition.type === "double") {
                //    const value = (adskProp.displayValue.toString() || "").replace(",", ".");
                //    item.reportValue = !_.isEmpty(info.propertyDefinition.format)
                //        ? numeral(value).format(info.propertyDefinition.format).replace(".", ",")
                //        : value || "";
                //}
                //else
                //    item.reportValue = adskProp.displayValue;

                this.propertyValues[info.propertyDefinition.name] = propValue;

                //this.reportData.push(item);
            }
        }
    }

    public updateNodeInfoToExternalData() {
        if (!viewerOptions.externalData) {
            var propertyDefinitions: { [key: string]: SmartDrawings.PropertyDefinition; } = {};

            for (let info of propertyInfo) {
                propertyDefinitions[info.propertyDefinition.name] = info.propertyDefinition;
            }
            let nodeData: SmartDrawings.NodeData = { propertyDefinitions: propertyDefinitions, propertyValues: {} };
            let data: SmartDrawings.ExternalData = { propertyDefinitions: {}, propertyValues: {}, nodes: nodeData };
            viewerOptions.externalData = data;
        }

        let { externalData } = viewerOptions as SmartDrawings.Model;

        externalData.nodes.propertyValues[this.id] = this.propertyValues;
    }

    public async equals(group: BalconyGroup): Promise<boolean> {
        // TODO: Compare only same BalconyNode types
        var same = true;
        //this.reportData.forEach(info => {
        //    if (same && info.includeInClassification) {
        //        let found = group.reportData.find(i => i.reportGroup === info.reportGroup && i.reportName === info.reportName && i.reportValue === info.reportValue);
        //        if (!found) same = false;
        //    }
        //});

        for (const key in this.propertyValues) {
            let info = getPropertyInfoItem(key);
            if (info && info.reportInfo.includeInClassification) {
                same = group.propertyValues.hasOwnProperty(key) && this.propertyValues[key] === group.propertyValues[key];
                if (!same) break;
            }
        }

        return same;
    }

    public async drawPanels(gfx: GfxUtils) {
        if (this.panels && this.panels.length > 0) {
            await gfx.addModel();
            this.panels.forEach(async (panel) => {
                await gfx.drawPlaneNode(panel.id, panel.baseLine.start.toViewerUnits(), panel.baseLine.end.toViewerUnits(),
                    panel.baseLine.center().toViewerUnits().add(new THREE.Vector3(0, 0, panel.height * viewerOptions.unitData.modelToViewerScale)), panel.color);
            });
        }
    }

    public async removePanels(gfx: GfxUtils) {
        if (this.panels && this.panels.length > 0) {
            this.panels.forEach(async (panel) => {
                await gfx.deleteNode(panel.id);
            });
        }

        this.panels = [];
    }

    public formatPropertyValue(key: string) {
        if (this.propertyValues.hasOwnProperty(key)) {
            let info = getPropertyInfoItem(key);
            if (info) {
                if (info.propertyDefinition.type === "double") {
                    const formatted = this.propertyValues[key].replace(",", ".");
                    this.propertyValues[key] = !_.isEmpty(info.propertyDefinition.format)
                        ? numeral(formatted).format(info.propertyDefinition.format).replace(".", ",")
                        : formatted || "";
                }
            }
        }
    }

    public formatValue(key: string, value: string): string {
        var newValue: string = value;

        let info = getPropertyInfoItem(key);
        if (info) {
            if (info.propertyDefinition.type === "double") {
                const formatted = value.replace(",", ".");
                newValue = !_.isEmpty(info.propertyDefinition.format)
                    ? numeral(formatted).format(info.propertyDefinition.format).replace(".", ",")
                    : formatted || "";
            }
        }

        return newValue;
    }

    private calcShapePosition(viewer: Autodesk.Viewing.GuiViewer3D): THREE.Vector3 {
        if (this.panels && this.panels.length > 0) {
            let tempPanels = [...this.panels];
            tempPanels.sort((a, b) => { return b.baseLine.distance() - a.baseLine.distance() });
            return tempPanels[0].baseLine.center().add(new THREE.Vector3(0, 0, tempPanels[0].height / 2));
        }
        else
            return this.getObjectPosition(viewer, this.connectingIds[0]);

        //let bBox = this.getObjectBoundingBox(viewer, this.connectingIds[0]);
        //let sphere = bBox.getBoundingSphere(new THREE.Sphere());

        //// Only one glass row
        //if (!this.propertyValues.hasOwnProperty("GlazingWidth2")) {
        //    return sphere.center.toModelUnits();
        //}

        //return bBox.max.toModelUnits();
    }

    private getObjectBoundingBox(viewer: Autodesk.Viewing.GuiViewer3D, dbId: number): THREE.Box3 {
        let fragIds: number[] = [];
        viewer.getVisibleModels()[0].getInstanceTree().enumNodeFragments(dbId, (fragId) => { fragIds.push(fragId); }, true);
        let fragList = viewer.getVisibleModels()[0].getFragmentList();

        let gfxUtils = new GfxUtils(viewer, "BalconyGroup");
        return gfxUtils.getModifiedWorldBoundingBox(fragIds, fragList);
    }

    private getObjectPosition(viewer: Autodesk.Viewing.GuiViewer3D, dbId: number): THREE.Vector3 {
        let fragIds: number[] = [];
        viewer.getVisibleModels()[0].getInstanceTree().enumNodeFragments(dbId, (fragId) => { fragIds.push(fragId); }, true);

        let fragProxy = viewer.impl.getFragmentProxy(viewer.getVisibleModels()[0], fragIds[0]);
        //let fragProxy = (viewer.getVisibleModels()[0] as any).getFragmentPointer(fragIds[0]);
        let matrix = new THREE.Matrix4();
        fragProxy.getWorldMatrix(matrix);
        return new THREE.Vector3().setFromMatrixPosition(matrix).toModelUnits();
    }

    private objectToVector3(obj) {
        return new THREE.Vector3(obj.x, obj.y, obj.z);
    }

    private objectToColor(obj) {
        return new THREE.Color(obj.r, obj.g, obj.b);
    }
}

export interface IBalconyClass {
    classType: string; // A, B, C, D, E
    groups: BalconyGroup[];
    color: THREE.Color;
}

export interface IReportInfo {
    includeInClassification: boolean;
    includeInReport: boolean;
    reportGroup: string; // lasitus tai kaide
    reportName: string; // pituus tai korkeus, asunto, kerros
    order: number;
    reportValue: string | number; // 150, RAL306
}

export interface IBalconyNode extends INode {
    type: BalconyNodeType;
}

export class BalconyNode implements IBalconyNode {
    id: number;
    color: THREE.Color;
    type: BalconyNodeType;
    position: THREE.Vector3;
    connectingIds: number[];
    propertyValues: { [key: string]: string };

    private viewer: Autodesk.Viewing.GuiViewer3D;

    private gfxUtils: GfxUtils;

    constructor(forgeViewer: Autodesk.Viewing.GuiViewer3D, nodeId: number, connectedParts: number[], nodeType: BalconyNodeType) {
        this.viewer = forgeViewer;
        this.id = nodeId;
        this.color = new THREE.Color(1, 1, 0);
        this.type = nodeType;
        this.connectingIds = connectedParts;
        this.position = this.calcShapePosition();
    }

    private calcShapePosition(): THREE.Vector3 {
        var nodebBox = new THREE.Box3();
        this.connectingIds.forEach((id) => {
            let bBox = this.getObjectBoundingBox(id);
            nodebBox.union(bBox);
        });
        
        let sphere = nodebBox.getBoundingSphere(new THREE.Sphere());
        let sphereCenter = new THREE.Vector3(sphere.center.x, sphere.center.y, sphere.center.z);
        // Convert position to default units (mm)
        return ViewerUtils.convertVectorFromViewerUnits(sphereCenter,
            UnitConstants.DEFAUT_MODELUNIT, this.viewer);
    }

    private getObjectBoundingBox(dbId: number): THREE.Box3 {
        let fragIds: Number[] = [];
        this.viewer.getVisibleModels()[0].getInstanceTree().enumNodeFragments(dbId, (fragId) => { fragIds.push(fragId); }, true);
        let fragList = this.viewer.model.getFragmentList();

        this.gfxUtils = new GfxUtils(this.viewer, "BalconyNode");
        return this.gfxUtils.getModifiedWorldBoundingBox(fragIds, fragList);
    }
}

export class BalconyPanel {
    id: number;
    baseLine: THREE.Line3;
    height: number;
    color: THREE.Color;
    modelId: number;

    constructor(nodeId: number, baseStart: THREE.Vector3, baseEnd: THREE.Vector3,
        height: number, color: THREE.Color, modelId: number) {
        this.id = nodeId;
        this.baseLine = new THREE.Line3(baseStart, baseEnd);
        this.height = height;
        this.color = color;
        this.modelId = modelId;
    }

    public copyWithOffSet(newId: number, offset: THREE.Vector3): BalconyPanel {
        return new BalconyPanel(newId, this.baseLine.start.clone().add(offset),
            this.baseLine.end.clone().add(offset), this.height, this.color.clone(), this.modelId);
    }
}